/**
 * OCC intents to drill (well permits) for the deals map. Pure.
 *
 * Permits come from GET /api/spatial/occ/intents?state=OK&county=…, one Point
 * feature per permit. Several permits often sit on the same surface location
 * (a multi-well pad), so every distinct point ("spot") is drawn once and the
 * hover lists every permit there, newest first — the same shape as the OCC
 * unit footprints (utils/occFootprints).
 *
 * WINDOW. Shown under "OCC filings, last 12 months"; the endpoint applies no
 * date filter, so the window is applied here: a permit is kept when its
 * approval date (filed date when there is none) is within `windowDays`
 * (default 365) of `now`.
 */
import { parseDateMs, formatDateOnly } from "./dateFormat";
import { titleCase } from "./metadataV2Columns";
import { footprintKey } from "./occFootprints";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** A permit's window date: approval date, else filed date (epoch ms or null). */
export const intentDateMs = (props) =>
  parseDateMs(props?.approval_date) ?? parseDateMs(props?.filed_date);

/**
 * @param {Array<object>} features  OCC intent Point features
 * @param {{ now?: number, windowDays?: number }} [options]
 * @returns {{ fc: object, intentsById: Map<string, object[]> }}
 *   fc: one Point feature per spot, props { spot_id, intent_count };
 *   intentsById: spot_id -> permit props, newest first
 */
export const groupOccIntents = (features, options = {}) => {
  const now = options.now ?? Date.now();
  const cutoff = now - (options.windowDays ?? 365) * MS_PER_DAY;

  const groups = new Map();
  for (const f of Array.isArray(features) ? features : []) {
    const p = f?.properties || {};
    const when = intentDateMs(p);
    if (when == null || when < cutoff) continue;
    const key = footprintKey(f.geometry);
    if (!key) continue;
    let g = groups.get(key);
    if (!g) {
      g = { geometry: f.geometry, intents: [] };
      groups.set(key, g);
    }
    g.intents.push({ ...p, _when: when });
  }

  const fc = { type: "FeatureCollection", features: [] };
  const intentsById = new Map();
  let n = 0;
  for (const g of groups.values()) {
    g.intents.sort((a, b) => b._when - a._when);
    const id = `in${n++}`;
    fc.features.push({
      type: "Feature",
      geometry: g.geometry,
      properties: { spot_id: id, intent_count: g.intents.length },
    });
    intentsById.set(
      id,
      g.intents.map(({ _when, ...rest }) => rest) // eslint-disable-line no-unused-vars
    );
  }
  return { fc, intentsById };
};

/**
 * The lines shown per permit in the hover. Labels from `occ_intents`
 * metadata (title-cased field name when a row is missing); empty values are
 * skipped.
 *
 *   well name + number | API # | operator | purpose of filing | wellbore
 *   profile | formations | TRS + quarter calls | approval date | permit
 *   cancelled date when cancelled, else permit expired date
 *
 * @returns {Array<{field:string,label:string,text:string}>}
 */
export const occIntentRows = (intent, metadata) => {
  const p = intent || {};
  const meta = Array.isArray(metadata) ? metadata : [];
  const label = (field) => meta.find((m) => m?.field === field)?.label || titleCase(field);
  const rows = [];
  const push = (field, text) => {
    if (text === null || text === undefined || text === "") return;
    rows.push({ field, label: label(field), text: String(text) });
  };
  const joined = (sep, ...vals) =>
    vals
      .map((v) => (v === null || v === undefined ? "" : String(v).trim()))
      .filter(Boolean)
      .join(sep);

  push("well_name", joined(" ", p.well_name, p.well_num));
  push("api_number", p.api_number);
  push("operator", p.operator);
  push("purpose_of_filing", p.purpose_of_filing);
  push("wellbore_profile", p.wellbore_profile);
  push("formations", p.formations);
  push("trs", joined(" · ", p.trs, p.quarter_calls));
  push("approval_date", formatDateOnly(p.approval_date));
  if (p.permit_cancelled_date) {
    push("permit_cancelled_date", formatDateOnly(p.permit_cancelled_date));
  } else {
    push("permit_expired_date", formatDateOnly(p.permit_expired_date));
  }
  return rows;
};
